import React, { useState, useEffect } from 'react';
import { getReminders } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bell, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';

const reminderTypes = ['Document Expiry', 'Invoice Due', 'Payment Overdue', 'Service Update', 'License Expiry'];

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    loadReminders();
  }, [filterType]);

  const loadReminders = async () => {
    try {
      const params = { limit: 100 };
      if (filterType) params.reminder_type = filterType;
      
      const res = await getReminders(params);
      setReminders(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'Email': return <Mail className="w-3 h-3" />;
      case 'WhatsApp': return <MessageSquare className="w-3 h-3" />;
      case 'SMS': return <Smartphone className="w-3 h-3" />;
      default: return <Bell className="w-3 h-3" />;
    }
  };

  const getDaysNoticeBadge = (days) => {
    if (days === 'Overdue' || parseInt(days) < 0) {
      return 'status-expired';
    }
    const d = parseInt(days);
    if (d <= 7) return 'status-critical';
    if (d <= 30) return 'status-warning';
    if (d <= 60) return 'status-upcoming';
    return 'status-valid';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reminders Log</h1>
          <p className="text-muted-foreground">View sent reminder notifications</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filterType} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {reminderTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : reminders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No reminders sent yet</p>
              <p className="text-sm mt-2">Reminders are automatically generated when documents are expiring</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Days Notice</TableHead>
                    <TableHead>Sent Via</TableHead>
                    <TableHead>Sent To</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reminders.map((r) => (
                    <TableRow key={r.id} data-testid={`reminder-row-${r.id}`}>
                      <TableCell>
                        <Badge variant="outline">{r.reminder_type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{r.message}</TableCell>
                      <TableCell>
                        <Badge className={getDaysNoticeBadge(r.days_notice)}>
                          {r.days_notice}d
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(r.sent_via || []).map((channel) => (
                            <Badge key={channel} variant="secondary" className="text-xs">
                              {getChannelIcon(channel)}
                              <span className="ml-1">{channel}</span>
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{r.sent_to || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(r.sent_at)}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === 'Sent' ? 'default' : 'destructive'}>
                          {r.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {reminders.length} of {total} reminders
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
